import styled from '@emotion/styled';
import { AnimatePresence } from 'framer-motion';
import { useEffect, useState } from 'react';
import { Switch, Route, useRouteMatch, useLocation } from 'react-router-dom';

import { SCOPES } from '../../../lib/scopes';
import { RouteManager } from '../../../RouteManager';
import { ToastManager } from '../../../ToastManager';

import { useEventCallback } from '../../../hooks/useEventCallback';
import { useI18n } from '../../../hooks/useI18nFormatters';
import { useApi } from '../../../store/SelectedHomeyStore.hooks';

import { scrollbars } from '../../../theme/elements/scrollbars';
import { su } from '../../../theme/functions/su';
import { glyph } from '../../../theme/glyph';
import { theme } from '../../../theme/theme';

import { OutlineButton } from '../../../components/buttons/OutlineButton';
import { CenteredMessage } from '../../../components/common/CenteredMessage';
import { ContextMenu } from '../../../components/common/context-menu/ContextMenu';
import { ContextMenuButton } from '../../../components/common/context-menu/ContextMenuButton';
import { Glyph } from '../../../components/common/Glyph';
import { ExternalLink } from '../../../components/common/link/ExernalLink';
import { RoundIconWrapper } from '../../../components/common/RoundIconWrapper';
import { SpinnerLoading } from '../../../components/common/SpinnerLoading';
import { Heading3Medium } from '../../../components/common/text/Heading3Medium';
import { ParagraphRegular } from '../../../components/common/text/ParagraphRegular';
import { TextRegular } from '../../../components/common/text/TextRegular';
import { TextSmallMedium } from '../../../components/common/text/TextSmallMedium';

import { ApiKeysSettingsContextMenuContent } from './ApiKeysSettingsContextMenuContent';
import { ApiKeysSettingsDialogCreate } from './ApiKeysSettingsDialogCreate';
import { ApiKeySettingsDialogDelete } from './ApiKeysSettingsDialogDelete';
import { ApiKeysSettingsDialogView } from './ApiKeysSettingsDialogView';

const contextMenuButtonClass = 'apiKeysContextMenuButton';

export function ApiKeysSettings() {
  const { i18n } = useI18n();
  const { api, scopes } = useApi();

  // Matches the parent route.
  const routeMatch = useRouteMatch();
  const location = useLocation();

  const [pats, setPats] = useState(null);

  const refresh = useEventCallback(() => {
    if (api && scopes[SCOPES.EVERYTHING] === true) {
      api
        .call({
          method: 'GET',
          path: '/api/manager/users/pat',
          body: undefined,
          $timeout: 40000,
        })
        .then((result) => {
          setPats(result);
        })
        .catch(ToastManager.handleError);
    }
  });

  useEffect(() => {
    refresh();
  }, [refresh]);

  const patsList = Object.values(pats ?? {});

  function getRoot() {
    if (scopes[SCOPES.EVERYTHING] !== true) {
      return (
        <S.Root>
          <CenteredMessage
            title={i18n.messageFormatter('common.insufficientPermissions')}
            subtitleColor={theme.color.text_light}
            subtitle={i18n.messageFormatter('common.insufficientPermissionsViewSubtitle', {
              name: i18n.messageFormatter('settings.system.apiKeys'),
            })}
          />
        </S.Root>
      );
    }

    if (pats == null) {
      return (
        <S.Root>
          <CenteredMessage
            icon={<SpinnerLoading color={theme.color.text_light} size={40} />}
            subtitleColor={theme.color.text_light}
            subtitle={i18n.messageFormatter('status.loading')}
          />
        </S.Root>
      );
    }

    if (patsList.length === 0) {
      return (
        <S.Root data-center="true">
          <S.WrapperEmpty>
            <RoundIconWrapper size="80px" color={theme.color.system_api_keys}>
              <Glyph
                size={theme.icon.size_xxlarge}
                source={glyph.system.apiKeys}
                color={theme.color.white}
              />
            </RoundIconWrapper>

            <div>
              <S.EmptyTitle textAlign="center">
                {i18n.messageFormatter('settings.system.apiKeys')}
              </S.EmptyTitle>
              <S.EmptyDescription color={theme.color.text_light}>
                {i18n.messageFormatter('settings.system.apiKeysNoKeys')}
              </S.EmptyDescription>
            </div>

            <S.ButtonWrapper>
              <OutlineButton
                styleWidth="full"
                styleIconColorDisabled={true}
                onPress={() => {
                  RouteManager.toCreatePat();
                }}
              >
                <RoundIconWrapper size={theme.icon.size_default} color={theme.color.blue}>
                  <OutlineButton.Icon
                    source={glyph.core.plusMedium}
                    size={theme.icon.size_small}
                    color={theme.color.white}
                  />
                </RoundIconWrapper>

                <OutlineButton.Text>
                  {i18n.messageFormatter('settings.system.apiKeysNew')}
                </OutlineButton.Text>
              </OutlineButton>
            </S.ButtonWrapper>

            <ExternalLink url="https://support.homey.app/hc/articles/8178797067292-Getting-started-with-API-Keys">
              {i18n.messageFormatter('settings.system.apiKeysHelp')}
            </ExternalLink>
          </S.WrapperEmpty>
        </S.Root>
      );
    }

    return (
      <S.Root>
        <S.Wrapper>
          <S.Title color={theme.color.text_light}>
            {i18n.messageFormatter('settings.system.apiKeys')}{' '}
            <ExternalLink url="https://support.homey.app/hc/articles/8178797067292-Getting-started-with-API-Keys">
              {i18n.messageFormatter('common.help')}
            </ExternalLink>
          </S.Title>

          <S.Container>
            {patsList.length > 0 &&
              patsList.map((pat) => {
                return (
                  <ContextMenu
                    key={pat.id}
                    content={<ApiKeysSettingsContextMenuContent pat={pat} />}
                  >
                    {({ isOpen, onContextMenu }) => {
                      return (
                        <S.Row onContextMenu={onContextMenu}>
                          <RoundIconWrapper size="24px" color={ApiKeysSettings.backgroundColor}>
                            <Glyph
                              source={glyph.system.apiKeys}
                              size={theme.icon.size_small}
                              color={theme.color.white}
                            />
                          </RoundIconWrapper>

                          <S.Name>{pat.name}</S.Name>
                          <S.Id color={theme.color.text_light}>{`...${pat.id}...`}</S.Id>
                          <S.Actions>
                            <ContextMenuButton
                              className={contextMenuButtonClass}
                              isOpen={isOpen}
                              onPress={onContextMenu}
                            />
                          </S.Actions>
                        </S.Row>
                      );
                    }}
                  </ContextMenu>
                );
              })}
          </S.Container>

          <S.ButtonWrapper>
            <OutlineButton
              styleWidth="full"
              styleIconColorDisabled={true}
              onPress={() => {
                RouteManager.toCreatePat();
              }}
            >
              <RoundIconWrapper size={theme.icon.size_default} color={theme.color.blue}>
                <OutlineButton.Icon
                  source={glyph.core.plusMedium}
                  size={theme.icon.size_small}
                  color={theme.color.white}
                />
              </RoundIconWrapper>

              <OutlineButton.Text>
                {i18n.messageFormatter('settings.system.apiKeysNew')}
              </OutlineButton.Text>
            </OutlineButton>
          </S.ButtonWrapper>
        </S.Wrapper>
      </S.Root>
    );
  }

  const routes = (
    <AnimatePresence mode="wait">
      <Switch location={location} key={location.pathname}>
        <Route
          path={`${routeMatch.path}/create`}
          render={() => {
            function handleCloseRequest() {
              RouteManager.toPath(`${routeMatch.url}`);
            }

            return (
              <ApiKeysSettingsDialogCreate refresh={refresh} onCloseRequest={handleCloseRequest} />
            );
          }}
        />

        <Route
          path={`${routeMatch.path}/:patId/delete`}
          render={() => {
            function handleCloseRequest() {
              RouteManager.toPath(`${routeMatch.url}`);
            }

            return (
              <ApiKeySettingsDialogDelete
                refresh={refresh}
                pats={pats}
                onCloseRequest={handleCloseRequest}
              />
            );
          }}
        />

        <Route
          path={`${routeMatch.path}/:patId`}
          render={() => {
            function handleCloseRequest() {
              RouteManager.toPath(`${routeMatch.url}`);
            }

            return <ApiKeysSettingsDialogView pats={pats} onCloseRequest={handleCloseRequest} />;
          }}
        />
      </Switch>
    </AnimatePresence>
  );

  return (
    <>
      {getRoot()}
      {routes}
    </>
  );
}

ApiKeysSettings.pathKey = 'system/api-keys';
ApiKeysSettings.icon = glyph.system.apiKeys;
ApiKeysSettings.backgroundColor = theme.color.system_api_keys;

function S() {}
ApiKeysSettings.S = S;

S.Root = styled.div`
  ${scrollbars.dark}
  display: flex;
  flex-direction: column;
  align-items: center;
  flex: 1 1 auto;
  margin-bottom: ${su(2)};
  overflow-y: auto;
  padding-top: ${su(3)};

  &[data-center='true'] {
    justify-content: center;
  }

  .${contextMenuButtonClass} {
    opacity: 1;
  }
`;

S.Wrapper = styled.div`
  width: min(calc(100% - ${su(5)}), 720px);
`;

S.WrapperEmpty = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: min(calc(100% - ${su(5)}), 720px);

  a {
    margin-top: ${su(2)};
  }
`;

S.EmptyTitle = styled(Heading3Medium)`
  margin-top: ${su(2)};
`;

S.EmptyDescription = styled(ParagraphRegular)`
  margin: ${su(1)} 0 ${su(2)};
`;

S.Title = styled(TextSmallMedium)`
  display: flex;
  justify-content: space-between;
  padding: 0 20px 16px;
`;

S.Message = styled.div`
  color: ${theme.color.text_light};
  font-size: ${theme.fontSize.regular};
  padding: ${su(2)};
`;

S.Container = styled.div`
  display: flex;
  flex-direction: column;
  background-color: ${theme.color.component};
  box-shadow: ${theme.boxShadow.default};
  border-radius: ${theme.borderRadius.default};
`;

S.Row = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  gap: ${su(1)};
  height: ${su(6)};
  padding-inline-start: ${su(2)};

  &:not(:last-child)::after {
    content: '';
    position: absolute;
    left: 0;
    right: 0;
    bottom: 0;
    height: 1px;
    background-color: ${theme.color.line};
  }
`;

S.Name = styled.div`
  max-width: 200px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

S.Id = styled(TextRegular)`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

S.Actions = styled.div`
  flex: 1 1 auto;
  display: flex;
  justify-content: flex-end;
  padding-inline-end: ${su(2)};
`;

S.ButtonWrapper = styled.div`
  width: 100%;
  padding: ${su(2)} 0;
`;
